import { styled } from "@linaria/react"
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover"
import { observer } from "mobx-react-lite"


import { Button } from "@/components/Button/Button.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading5 } from "@/components/Heading/Heading.tsx"
import { AlertFill, CloseLight, Icon20 } from "@/icons/Icons.ts"
import { dropdownShadow } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { ReactNode } from "react"

interface ProjectAuthErrorPopupProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  canEditProject: boolean
  goToSettings: () => void
  children?: ReactNode
}


export const ProjectAuthErrorPopup = observer(({ isOpen, onOpenChange, canEditProject, goToSettings, children }: ProjectAuthErrorPopupProps) => {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      {children && (
        <PopoverTrigger asChild>
          {/* The trigger element (e.g., icon button) comes from parent via children */}
          {children}
        </PopoverTrigger>
      )}
      <PopoverPortal>
        <StyledPopoverContent
          className={DropdownMenuStyles.Content}
          align="start"
          side="right"
          sideOffset={28}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={() => onOpenChange(false)}
        >
          <CloseButton kind="icon" aria-label="Close" onClick={() => onOpenChange(false)}>
            <CloseLight className={Icon20} />
          </CloseButton>

          <Horizontal gap="8px">
            <AlertButton color="var(--error-color)" />

            <Vertical gap="16px">
              <Vertical gap="8px">
                {!canEditProject && (
                  <>
                    <Heading5>Ask an admin to help</Heading5>
                    <Description>
                      Failed to fetch the latest version of the repository. Ideas will run from an older version. Ask an
                      administrator to update the credentials.
                    </Description>
                  </>
                )}

                {canEditProject && (
                  <>
                    <Heading5>Outdated repository version</Heading5>
                    <Description>
                      Failed to fetch the latest version of the repository. Ideas will run from an older version until
                      you update the credentials.
                    </Description>
                  </>
                )}
              </Vertical>

              {canEditProject && (
                <Vertical alignSelf="flex-start">
                  <ConnectButton kind="secondary" onClick={() => {
                    goToSettings()
                    onOpenChange(false)
                  }}>Update credentials</ConnectButton>
                </Vertical>
              )}
            </Vertical>
          </Horizontal>
        </StyledPopoverContent>
      </PopoverPortal>
    </Popover>
  )
})


const Description = styled.div`
  ${textStyles.smallestFontSize};

  line-height: 119%;

  color: var(--secondary-text-color);
`

const AlertButton = styled(AlertFill)`
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
`

const CloseButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 16px;

  width: 20px;
  height: 20px;
`

const ConnectButton = styled(Button)`
  font-weight: 400;
`

const StyledPopoverContent = styled(PopoverContent)`
  width: 344px;
  margin-left: -20px;
  padding: 16px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-20);

  background: linear-gradient(112deg, rgb(255 108 140 / 20%) 6.22%, rgb(255 255 255 / 20%) 45.27%), #FFF;

  ${dropdownShadow};

  z-index: var(--z-index-dropdown);
`
